import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useNavigationStore } from '@/stores/navigationStore'
import type { NavigationItem } from '@/types/navigation'
import { sortRootSections, sortSiblings } from '@/utils/navigationSort'
import { hasNavigationItemPermission } from '@/utils/navigationPermissionAccess'

function sortNavigationTree(items: NavigationItem[], isRoot = true): NavigationItem[] {
  const sorted = isRoot ? sortRootSections(items) : sortSiblings(items)

  return sorted.map((item) => ({
    ...item,
    children: item.children?.length ? sortNavigationTree(item.children, false) : item.children,
  }))
}

export function useNavigationConfig() {
  const navigationStore = useNavigationStore()
  const authStore = useAuthStore()

  const filterTreeByPermissions = (items: NavigationItem[]): NavigationItem[] => {
    return items
      .filter((item) => hasNavigationItemPermission(authStore.hasPermission, item.permission))
      .map((item) => ({
        ...item,
        children: item.children?.length ? filterTreeByPermissions(item.children) : item.children,
      }))
  }

  const loadNavigation = async (): Promise<NavigationItem[]> => {
    return await navigationStore.loadNavigation()
  }

  const visibleItems = computed(() => {
    const filtered = filterTreeByPermissions(navigationStore.items)
    return sortNavigationTree(filtered)
  })

  return {
    items: computed(() => navigationStore.items),
    visibleItems,
    loading: computed(() => navigationStore.loading),
    error: computed(() => navigationStore.error),
    lastLoadedAt: computed(() => navigationStore.lastLoadedAt),
    rootSections: computed(() => navigationStore.rootSections),
    loadNavigation,
    filterTreeByPermissions,
  }
}
